import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import type { AuditEntry } from '@/components/shared/audit-trail-panel';
import { downloadBlob } from '@/utils/download-blob';
import {
  drawPdfBrandHeaderRow,
  drawPdfPageFooters,
  drawRepeatPageHeader,
  loadReportPdfLogo,
  measureRepeatPageHeaderHeight,
  PDF_BRAND_TO_TITLE_GAP,
  PDF_FOOTER_ZONE,
  PDF_PAGE_MARGIN,
  toPdfText,
} from '@/utils/pdf-brand-header';
import { ensurePdfFonts, pdfFont } from '@/utils/pdf-fonts';

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

export interface FinancialAuditPdfInput {
  churchName: string;
  branchName?: string | null;
  logoUrl?: string | null;
  title: string;
  description: string;
  generatedLabel: string;
  /** Optional filter summary lines shown under the title. */
  metaLines?: string[];
  columns: {
    when: string;
    summary: string;
    actor: string;
    details: string;
  };
  emptyValue: string;
  filename: string;
  entries: AuditEntry[];
}

const formatChangeLine = (
  change: NonNullable<AuditEntry['changes']>[number],
  emptyValue: string,
): string => {
  if (change.before != null && change.before !== '' && change.after != null && change.after !== '') {
    return `${change.field}: ${change.before} -> ${change.after}`;
  }
  if (change.after != null && change.after !== '') {
    return `${change.field}: ${change.after}`;
  }
  return `${change.field}: ${change.before ?? emptyValue}`;
};

const formatDetails = (entry: AuditEntry, emptyValue: string): string => {
  const parts: string[] = [];
  if (entry.description) parts.push(entry.description);
  for (const change of entry.changes ?? []) {
    parts.push(formatChangeLine(change, emptyValue));
  }
  return parts.length > 0 ? parts.join('\n') : '-';
};

/** Financial activity PDF using Inter, the same family as church reports. */
export const downloadFinancialAuditPdf = async (input: FinancialAuditPdfInput): Promise<void> => {
  const logo = await loadReportPdfLogo(input.logoUrl);
  const branchName = input.branchName?.trim() || null;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' }) as JsPdfWithAutoTable;
  await ensurePdfFonts(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const repeatHeaderHeight = measureRepeatPageHeaderHeight(
    doc,
    pageWidth,
    input.title,
    input.churchName,
    branchName,
    logo,
  );

  const brand = drawPdfBrandHeaderRow(doc, pageWidth, PDF_PAGE_MARGIN, {
    churchName: input.churchName,
    branchName,
    logo,
  });

  let y = brand.leftBottomY + PDF_BRAND_TO_TITLE_GAP;

  doc.setFontSize(16);
  doc.setFont(pdfFont(), 'bold');
  doc.setTextColor(40, 40, 40);
  const titleLines = doc.splitTextToSize(toPdfText(input.title), contentWidth);
  doc.text(titleLines, PDF_PAGE_MARGIN, y);
  y += titleLines.length * 6 + 1;

  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  const descriptionLines = doc.splitTextToSize(toPdfText(input.description), contentWidth);
  doc.text(descriptionLines, PDF_PAGE_MARGIN, y);
  y += descriptionLines.length * 4 + 2;

  for (const line of input.metaLines ?? []) {
    doc.text(toPdfText(line), PDF_PAGE_MARGIN, y);
    y += 4;
  }

  doc.text(toPdfText(input.generatedLabel), PDF_PAGE_MARGIN, y);
  y += 6;

  y = Math.max(y, brand.blockBottomY) + 2;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(PDF_PAGE_MARGIN, y, pageWidth - PDF_PAGE_MARGIN, y);
  y += 4;

  const rows = input.entries.map((entry) => [
    toPdfText(entry.at),
    toPdfText(entry.summary),
    toPdfText(entry.actor ?? '-'),
    toPdfText(formatDetails(entry, input.emptyValue)),
  ]);

  autoTable(doc, {
    startY: y,
    head: [[
      toPdfText(input.columns.when),
      toPdfText(input.columns.summary),
      toPdfText(input.columns.actor),
      toPdfText(input.columns.details),
    ]],
    body: rows.length > 0 ? rows : [['-', '-', '-', '-']],
    theme: 'grid',
    styles: {
      font: pdfFont(),
      fontSize: 8,
      textColor: [40, 40, 40],
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
      valign: 'top',
      cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
      overflow: 'linebreak',
    },
    headStyles: {
      font: pdfFont(),
      fontStyle: 'bold',
      fillColor: [245, 245, 245],
      textColor: [40, 40, 40],
    },
    columnStyles: {
      0: { cellWidth: 32 },
      1: { cellWidth: 42 },
      2: { cellWidth: 28 },
      3: { cellWidth: 'auto' },
    },
    margin: {
      top: repeatHeaderHeight,
      left: PDF_PAGE_MARGIN,
      right: PDF_PAGE_MARGIN,
      bottom: PDF_PAGE_MARGIN + PDF_FOOTER_ZONE,
    },
    didDrawPage: (data) => {
      if (data.pageNumber === 1) return;
      drawRepeatPageHeader(doc, pageWidth, input.title, input.churchName, branchName, logo);
    },
  });

  drawPdfPageFooters(doc, pageWidth, pageHeight, input.churchName);
  downloadBlob(doc.output('blob'), input.filename);
};
